import { AuthShell } from '@/components/auth-shell'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  LockPasswordIcon,
  ViewIcon,
  ViewOffIcon,
  ArrowLeft01Icon,
} from '@hugeicons/core-free-icons'
import toast from 'react-hot-toast'

import { authClient } from '@/lib/auth-client'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group'

interface FormValues {
  newPassword: string
  confirmPassword: string
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)

  // Better Auth appends the token to the redirect URL. It can also report an
  // invalid/expired token via the `error` query param.
  const token = searchParams.get('token')
  const tokenError = searchParams.get('error')

  const form = useForm<FormValues>({
    defaultValues: { newPassword: '', confirmPassword: '' },
  })

  useEffect(() => {
    if (tokenError) {
      toast.error('El enlace de recuperación es inválido o expiró.')
    }
  }, [tokenError])

  const onSubmit = async (data: FormValues) => {
    if (!token) return
    if (data.newPassword !== data.confirmPassword) {
      form.setError('confirmPassword', {
        message: 'Las contraseñas no coinciden',
      })
      return
    }

    const { error } = await authClient.resetPassword({
      newPassword: data.newPassword,
      token,
    })

    if (error) {
      toast.error(error.message || 'No se pudo restablecer la contraseña')
      return
    }

    toast.success('Contraseña restablecida. Inicia sesión con la nueva.')
    navigate('/login', { replace: true })
  }

  const invalidLink = !token || !!tokenError

  return (
    <AuthShell>
      <Card variant="plain">
        <CardHeader>
          <div className="mb-2 flex size-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
            <HugeiconsIcon icon={LockPasswordIcon} className="size-6" />
          </div>
          <CardTitle variant="page">
            <h1>Nueva contraseña.</h1>
          </CardTitle>
          <CardDescription>
            Elige una contraseña nueva para tu cuenta.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {invalidLink ? (
            <div className="flex flex-col gap-4">
              <Alert variant="destructive">
                <AlertTitle>Enlace inválido</AlertTitle>
                <AlertDescription>
                  El enlace de recuperación es inválido o expiró. Pide uno
                  nuevo.
                </AlertDescription>
              </Alert>
              <Button asChild className="w-full">
                <Link to="/forgot-password">Solicitar un nuevo enlace</Link>
              </Button>
              <Button asChild variant="ghost" size="sm" className="self-center">
                <Link to="/login">
                  <HugeiconsIcon
                    icon={ArrowLeft01Icon}
                    data-icon="inline-start"
                  />
                  Volver a iniciar sesión
                </Link>
              </Button>
            </div>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex flex-col gap-6"
              >
                <FormField
                  control={form.control}
                  name="newPassword"
                  rules={{
                    required: 'Ingresa la nueva contraseña',
                    minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nueva contraseña</FormLabel>
                      <InputGroup>
                        <InputGroupAddon align="inline-start">
                          <HugeiconsIcon icon={LockPasswordIcon} />
                        </InputGroupAddon>
                        <FormControl>
                          <InputGroupInput
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder="Mínimo 8 caracteres"
                            {...field}
                          />
                        </FormControl>
                        <InputGroupAddon align="inline-end">
                          <InputGroupButton
                            type="button"
                            size="icon-xs"
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label={showPassword ? 'Ocultar' : 'Mostrar'}
                          >
                            <HugeiconsIcon
                              icon={showPassword ? ViewOffIcon : ViewIcon}
                            />
                          </InputGroupButton>
                        </InputGroupAddon>
                      </InputGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  rules={{ required: 'Confirma la nueva contraseña' }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirmar contraseña</FormLabel>
                      <FormControl>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          placeholder="Repite la nueva contraseña"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  size="lg"
                  disabled={form.formState.isSubmitting}
                  className="w-full"
                >
                  {form.formState.isSubmitting
                    ? 'Guardando…'
                    : 'Restablecer contraseña'}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  )
}
